import type { Engine } from '../types';
import { buildUrl } from '../url';
import { distanceParam, priceParam } from './shared';

const DAYS: Record<string, string> = { d: '1', w: '7', m: '30' };
const CONDITION: Record<string, string> = {
  new: 'new',
  used: 'used_like_new,used_good,used_fair',
  likenew: 'used_like_new',
  good: 'used_like_new,used_good',
};

// Distance means nothing once an item ships.
const localOnly = {
  when: { param: 'delivery', in: ['local_pick_up'], orUnset: true },
  whenLabel: 'doesn’t apply to shipped items',
};

export const marketplace: Engine = {
  id: 'marketplace',
  name: 'Facebook Marketplace',
  bang: 'fbm',
  aliases: ['marketplace', 'fb'],
  homepage: 'https://www.facebook.com/marketplace',
  category: 'Shopping',
  defaultOn: true,
  signIn: 'Marketplace needs you signed in to Facebook.',
  description: 'Local listings by price, condition and distance.',
  experimental: true,
  params: [
    {
      id: 'city',
      type: 'text',
      prefix: '@',
      setting: 'marketplaceCity',
      pattern: /^[a-z0-9-]{2,40}$/,
      label: 'City',
      group: 'Location',
      placeholder: 'tacoma-wa',
      help: 'Marketplace city slug: big metros are bare (nyc, denver), others are city-st (tacoma-wa). Defaults to Settings, then your Facebook location.',
    },
    priceParam,
    {
      id: 'listed',
      type: 'choice',
      label: 'Listed within',
      group: 'Filters',
      options: [
        { value: 'd', token: 'd', label: 'Last 24 hours' },
        { value: 'w', token: 'w', label: 'Last 7 days' },
        { value: 'm', token: 'm', label: 'Last 30 days' },
      ],
    },
    {
      id: 'condition',
      type: 'choice',
      label: 'Condition',
      group: 'Filters',
      options: [
        { value: 'new', token: 'new', label: 'New' },
        { value: 'likenew', token: 'likenew', label: 'Used – like new' },
        { value: 'good', token: 'good', label: 'Used – good or better' },
        { value: 'used', token: 'used', label: 'Any used' },
      ],
    },
    {
      id: 'delivery',
      type: 'choice',
      label: 'Delivery',
      group: 'Filters',
      options: [
        { value: 'local_pick_up', token: 'pickup', label: 'Local pickup' },
        { value: 'shipping', token: 'ship', label: 'Shipping' },
      ],
    },
    {
      ...distanceParam('Converted to km for Facebook.'),
      ...localOnly,
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        {
          value: 'creation_time_descend',
          token: 'newest',
          label: 'Newest first',
        },
        {
          value: 'distance_ascend',
          token: 'nearest',
          label: 'Nearest first',
          ...localOnly,
        },
        { value: 'price_ascend', token: 'low', label: 'Price: low to high' },
        { value: 'price_descend', token: 'high', label: 'Price: high to low' },
      ],
    },
  ],
  build({ query, values, settings }) {
    const city =
      typeof values.city === 'string' ? values.city : settings.marketplaceCity;
    const base = city
      ? `https://www.facebook.com/marketplace/${city}/search`
      : 'https://www.facebook.com/marketplace/search';
    const price = typeof values.price === 'object' ? values.price : {};

    return buildUrl(base, {
      query,
      minPrice: price.min,
      maxPrice: price.max,
      daysSinceListed: DAYS[String(values.listed)],
      itemCondition: CONDITION[String(values.condition)],
      deliveryMethod: values.delivery as string | undefined,
      radius:
        typeof values.distance === 'number'
          ? Math.round(values.distance * 1.609)
          : undefined,
      sortBy: values.sort as string | undefined,
    });
  },
};
